import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Table } from "@mui/material";

/**
 * 열 너비를 드래그로 조절할 수 있는 MUI Table 래퍼 (컨테이너 100% 채움형).
 *
 * 사용법 — 기존 `<Table ...>` 을 `<ResizableTable ...>` 로만 바꾸면 된다.
 * 나머지 props(size·sx·stickyHeader 등)와 자식(TableHead/TableBody)은 그대로 내부 MUI Table 로 전달되므로
 * 공통 스타일(ds.tableSx)·정렬·페이지네이션 등 기존 동작에는 영향이 없다.
 *
 * 폭 정책(중요):
 *  - 표는 항상 컨테이너 폭 100% 를 채운다(`width:100%` + `table-layout:fixed`).
 *  - 각 열 = <b>바닥(minColWidth) + 여유분 × 비율</b>. 먼저 모든 열에 minColWidth 를 깔고,
 *    그 위에 남는 폭만 비율대로 나눈다 → 넓으면 비율대로 늘어나 100% 를 채우고, 좁아져도
 *    <b>어떤 열도 minColWidth 밑으로는 내려가지 않는다.</b>
 *    (예전엔 열 폭이 순수 비율이라, 폭이 내용이 아니라 컨트롤 크기로 정해지는 열 — 대표적으로
 *     체크박스 열(38px + 셀 패딩 4px = 42px 필요) — 이 표가 좁아지면 같이 줄다가 아래 overflow:hidden
 *     에 걸려 잘렸다. 연락처 표에서 실제로 발생했고, 측정 시점보다 5% 만 좁아져도 잘렸다.
 *     자연폭이 바닥보다 좁은 열은 여유분 몫이 0 이 되어 항상 바닥폭 그대로 유지된다.)
 *  - 가로 스크롤은 **열이 정말 안 들어갈 때만** 생긴다: 컨테이너 폭 < Σ(열 최소폭) 이면
 *    `min-width`(= 열 개수 × minColWidth)에 걸려 넘치고, 그때만 래퍼가 가로 스크롤한다.
 *    (예전 버전은 측정한 자연폭 합으로 width/minWidth 를 고정해, 열이 적어도 컨테이너보다
 *     조금만 좁으면 항상 스크롤이 생기고 넓어도 100% 를 못 채웠다 — 그 두 원인을 제거.)
 *  - 세로 스크롤은 만들지 않는다(래퍼는 overflowX 만). 행 수 제한은 페이지네이션이 담당.
 *
 * 리사이즈:
 *  - 열 경계를 끌면 그 열을 늘리거나 줄이고, "그 뒤의 모든 열" 이 비율대로 폭을 내주거나 받아
 *    합(100%)을 유지한다 → 가로 스크롤이 생기지 않는다.
 *    (예전엔 그 열과 "바로 다음 열" 사이에서만 주고받아, 다음 열이 최소폭에 닿는 순간 뒤 열에
 *     여유가 남아도 더 못 넓혀 긴 텍스트가 잘렸다 — 그 한계를 제거.)
 *  - 조절값은 저장하지 않는다(세션 한정). 열 개수가 바뀌면(권한/탭) 자연폭 비율로 다시 측정한다.
 *
 * 주의: 헤더가 colSpan 으로 그룹핑된 표는 '가장 셀이 많은 헤더 행'을 기준 열로 잡는다.
 *
 * @param {number} [minColWidth=56]  열 <b>바닥</b> 너비(px) — 어떤 열도 이 아래로는 안 내려간다.
 *   비율 배분은 이 바닥 위 여유분에만 적용된다. Σ(minColWidth) 가 곧 가로 스크롤 임계폭.
 * @param {*}      [resetKey]        이 값이 바뀌면 열 비율을 버리고 자연폭으로 다시 측정한다.
 *   열 <b>개수</b>가 같아도 열의 <b>성격</b>이 바뀌는 화면에서 필요하다. 예: 연락처 표는 탭에 따라
 *   첫 열이 체크박스(≈44px)였다가 번호가 되는데, 둘 다 7열이라 개수 검사만으로는 재측정이 일어나지
 *   않아 체크박스용 좁은 폭이 번호 열에 그대로 적용돼 "번호" 가 잘렸다. 그런 화면은 탭 키를 넘겨준다.
 */

// 열 기준이 되는 헤더 셀들(가장 셀이 많은 thead 행). 없으면 첫 tbody 행.
function headerCells(table) {
    if (!table) return [];
    let best = [];
    table.querySelectorAll("thead tr").forEach(tr => {
        if (tr.children.length > best.length) best = Array.from(tr.children);
    });
    if (!best.length) {
        const br = table.querySelector("tbody tr");
        if (br) best = Array.from(br.children);
    }
    return best;
}

/**
 * 경계 i 를 delta(표 폭 대비 비율, 부호 있음) 만큼 끈 결과의 새 열 비율(합 = 1).
 *  delta > 0 : 열 i 가 넓어지고, 뒤 열들(i+1..)이 "각자 여유(현재-최소)" 비율로 줄어든다.
 *              뒤 열들이 내줄 수 있는 총여유까지만 넓어진다(그 이상은 클램프).
 *  delta < 0 : 열 i 가 자기 여유(현재-최소)만큼만 좁아지고, 뒤 열들이 같은 "여유" 비율로 넓어진다.
 *              단 뒤에 넓어질 수 있는 열이 하나도 없으면(총여유 0) 열 i 도 못 좁힌다 → 합 100% 유지.
 * 여유가 0인 열(최소폭 이하: 체크박스 등)은 늘지도 줄지도 않아 고정된다.
 * start(드래그 시작 스냅샷)와 delta 만의 순수 함수 → 같은 위치로 끌면 항상 같은 결과(경로 무관).
 */
function redistribute(start, i, delta, minFrac) {
    const n = start.length;
    const minOf = (k) => Math.min(minFrac, start[k]);
    const room = [];                       // 뒤 열들의 여유폭(현재-최소, 음수는 0)
    let total = 0;
    for (let k = i + 1; k < n; k++) { const r = Math.max(0, start[k] - minOf(k)); room.push(r); total += r; }
    const up = total;                                       // 열 i 최대 확대 = 뒤 열 총여유
    const down = total > 0 ? Math.max(0, start[i] - minOf(i)) : 0;   // 열 i 최대 축소(받아줄 열 있을 때만)
    const d = Math.max(-down, Math.min(up, delta));         // 이동분을 가능 범위로 클램프
    const out = start.slice();
    out[i] = start[i] + d;
    if (total > 0) for (let idx = 0; idx < room.length; idx++) out[i + 1 + idx] = start[i + 1 + idx] - d * (room[idx] / total);
    return out;
}

export default function ResizableTable({ minColWidth = 56, resetKey, sx, children, ...rest }) {
    const tableRef = useRef(null);
    const fracRef  = useRef(null);      // 현재 열 비율(합 = 1)
    const dragRef  = useRef(null);
    const [fractions, setFractions] = useState(null);
    const [tableW, setTableW] = useState(0);   // 렌더된 표 실제 폭(px) — 손잡이 위치 계산용
    const [tableH, setTableH] = useState(0);
    useEffect(() => { fracRef.current = fractions; }, [fractions]);

    const commit = useCallback((next) => { fracRef.current = next; setFractions(next); }, []);

    const syncDims = useCallback((table) => {
        setTableW(table.offsetWidth || 0);
        setTableH(table.offsetHeight || 0);
    }, []);

    // 열 비율 측정 — 아직 없거나(널) 열 개수가 달라졌을 때만 새로 잡는다.
    // 컨테이너 리사이즈 시엔 비율은 유지하고 표 실측 폭(tableW)만 갱신 → 손잡이가 따라 움직인다.
    const measure = useCallback(() => {
        const table = tableRef.current;
        if (!table) return;
        const cells = headerCells(table);
        const n = cells.length;
        if (!n) return;
        const cur = fracRef.current;
        if (cur && cur.length === n) { syncDims(table); return; }   // 비율 유지, 치수만 갱신
        // 열 개수가 바뀌면(권한/탭에 따라 컬럼 증감) 먼저 비율을 리셋한다.
        // 그래야 다음 렌더에서 고정 레이아웃·낡은 colgroup 이 걷힌 '자연폭' 상태로 다시 측정된다
        // (리셋 없이 재측정하면 낡은 colgroup 아래 왜곡된 폭이 굳어버림).
        if (cur && cur.length !== n) { commit(null); return; }
        const w = cells.map(c => c.getBoundingClientRect().width);
        if (w.some(x => x <= 0)) return;                            // 아직 렌더/표시 전
        // 모든 열에 minColWidth 를 바닥으로 깔고, 그 위 "여유분" 만 비율로 나눈다.
        // 자연폭이 바닥보다 좁은 열(체크박스 48px)은 여유분이 0 → 늘 바닥폭 그대로라 절대 안 줄어든다.
        const room  = w.map(x => Math.max(0, x - minColWidth));
        const total = room.reduce((a, b) => a + b, 0);
        // 모든 열이 바닥보다 좁으면 나눌 여유분이 없다(합 0) → 비율이 전부 0 이 되어 표를 다 못 채운다.
        // 이때만 균등 분배로 떨어뜨린다(브라우저 폴백에 맡기지 않고 명시).
        commit(total > 0 ? room.map(x => x / total) : room.map(() => 1 / n));
        syncDims(table);
    }, [commit, syncDims, minColWidth]);

    // resetKey 가 바뀌면 비율을 버린다 → 다음 렌더에서 colgroup·고정 레이아웃이 걷힌
    // '자연폭' 상태로 다시 측정된다(열 개수가 그대로여도 성격이 바뀐 경우를 위한 장치).
    useEffect(() => {
        if (resetKey === undefined) return;
        commit(null);
    }, [resetKey, commit]);

    useLayoutEffect(() => { measure(); });                          // 매 렌더 후 시도

    // 표 크기 변화(표시 전환·컨테이너 리사이즈) 감지 → 재측정/치수 갱신
    useEffect(() => {
        const table = tableRef.current;
        if (!table || typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver(() => { measure(); syncDims(table); });
        ro.observe(table);
        syncDims(table);
        return () => ro.disconnect();
    }, [measure, syncDims]);

    // 경계 i 드래그 = 열 i 를 늘리고/줄이고, "그 뒤의 모든 열" 이 비율대로 폭을 주고받는다(합 100% 유지).
    const onHandleDown = (i) => (e) => {
        if (e.button != null && e.button !== 0) return;
        if (dragRef.current) return;
        e.preventDefault(); e.stopPropagation();
        const cur = fracRef.current;
        const table = tableRef.current;
        if (!cur || !table || i + 1 >= cur.length) return;
        // 비율은 "바닥 위 여유분" 기준이므로 px↔비율 환산 분모도 표 폭이 아니라 여유분이어야
        // 끈 거리와 실제 이동폭이 1:1 로 맞는다. 바닥은 이미 깔려 있으니 최소 비율은 0 —
        // 열은 아무리 좁혀도 바닥폭(minColWidth)까지만 줄어든다.
        const flexW = Math.max(1, (table.offsetWidth || 0) - cur.length * minColWidth);
        const minFrac = 0;
        const pointerId = e.pointerId;
        try { e.currentTarget.setPointerCapture?.(pointerId); } catch { /* ignore */ }
        // start = 드래그 시작 시점의 전체 열 비율 스냅샷. 이후 이동분(pending)은 이 스냅샷 기준
        // 순수 함수(redistribute)로 재계산 → 같은 위치로 끌면 항상 같은 결과(경로 무관).
        dragRef.current = { i, pointerId, startX: e.clientX, flexW, start: cur.slice(), minFrac, raf: 0, pending: 0 };

        const onMove = (ev) => {
            const d = dragRef.current; if (!d || ev.pointerId !== d.pointerId) return;
            d.pending = (ev.clientX - d.startX) / d.flexW;   // px → 비율(부호 있음)
            if (!d.raf) d.raf = requestAnimationFrame(() => {
                d.raf = 0;
                if ((fracRef.current || []).length !== d.start.length) return;
                commit(redistribute(d.start, d.i, d.pending, d.minFrac));
            });
        };
        const cleanup = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
        const onUp = (ev) => {
            const d = dragRef.current;
            if (d && ev.pointerId !== d.pointerId) return;
            dragRef.current = null;
            if (d) {
                if (d.raf) cancelAnimationFrame(d.raf);
                // 마지막 rAF 이후~release 사이 이동분을 확정 반영(놓은 위치가 항상 적용되게)
                if ((fracRef.current || []).length === d.start.length)
                    commit(redistribute(d.start, d.i, d.pending, d.minFrac));
            }
            cleanup();
        };
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onUp);
    };

    // 열 폭 = 바닥(minColWidth) + 여유분 × 비율. 비율 합이 1 이라 총합은 정확히 표 폭 —
    // 넘치지도, 오른쪽에 빈 띠가 남지도 않는다.
    const minTotal = fractions ? fractions.length * minColWidth : undefined;
    const extra    = fractions && tableW ? Math.max(0, tableW - minTotal) : 0;
    const colPx    = fractions ? fractions.map(f => minColWidth + extra * f) : null;

    // 손잡이 위치 = 앞 열들의 실제 px 누적 (표 좌표계 — 스크롤 시 내용과 함께 이동)
    const boundaries = [];
    if (colPx && tableW) {
        let x = 0;
        for (let i = 0; i < colPx.length - 1; i++) { x += colPx[i]; boundaries.push({ i, left: x }); }
    }

    return (
        <Box sx={{
            position: "relative", width: "100%",
            /*
             * 이 래퍼는 <b>스크롤 컨테이너가 되지 않는다</b>(overflow:visible).
             *
             * 표 높이는 행 수만큼 그대로 늘어나고, 스크롤은 바깥(페이지 또는 패널)이 담당한다.
             * 여기에 overflow:auto 를 주면 래퍼가 스크롤 컨테이너가 되어 <b>헤더 틀고정이 깨진다</b> —
             * sticky 는 가장 가까운 스크롤 컨테이너 기준으로 붙는데, 이 래퍼는 내용만큼 높이가 자라
             * 자기 안에서는 스크롤이 일어나지 않기 때문이다(실제 스크롤은 바깥이 하는데 기준은 여기가 됨).
             * visible 로 두면 sticky 기준이 실제로 스크롤하는 바깥 컨테이너가 되어 헤더가 제자리에 붙는다.
             *
             * 가로 넘침 처리는 바깥 컨테이너(ds.card 의 overflowX:clip 등)에 맡긴다.
             */
            overflow: "visible",
        }}>
            <Table
                ref={tableRef}
                {...rest}
                sx={{
                    ...sx,
                    // 100% 채움 + 비율 열. minWidth 는 "열 최소폭 합" — 여기 미만으로 좁아질 때만 가로 스크롤.
                    ...(fractions ? {
                        tableLayout: "fixed",
                        width: "100%",
                        minWidth: minTotal,
                        "& td, & th": { overflow: "hidden" },
                    } : {}),
                    // 헤더 틀고정(sticky)은 쓰지 않는다 — 표는 자연 높이로 늘어나고 스크롤은 부모가 하므로
                    // 헤더도 내용과 함께 흐른다. 호출처가 를 넘겨도 여기서 강제하지 않는다.
                }}
            >
                {colPx && (
                    <colgroup>
                        {/* % 가 아니라 px 로 준다 — Chromium 은 col 의 width 에 퍼센트가 섞인 calc() 를
                            무시하고 전 열을 균등폭으로 폴백해버린다(Chrome 148 실측). tableW 는
                            ResizeObserver 가 갱신하므로 표 폭이 바뀌면 이 값도 따라 다시 계산된다. */}
                        {colPx.map((px, i) => <col key={i} style={{ width: `${px}px` }} />)}
                    </colgroup>
                )}
                {children}
            </Table>

            {/* 열 경계 드래그 손잡이 */}
            {boundaries.map(({ i, left }) => (
                <Box
                    key={i}
                    onPointerDown={onHandleDown(i)}
                    onClick={e => { e.stopPropagation(); }}
                    sx={{
                        position: "absolute", top: 0, left: left - 4, width: 8, height: tableH || "100%",
                        cursor: "col-resize", zIndex: 4, touchAction: "none",
                        "&::after": {
                            content: '""', position: "absolute", left: 3.5, top: 0, width: "1px", height: "100%",
                            bgcolor: "transparent", transition: "background-color 0.12s",
                        },
                        "&:hover::after": { bgcolor: "rgba(37,99,235,0.5)" },
                    }}
                />
            ))}
        </Box>
    );
}
